import { useMemo } from 'react';
import { TourType } from '@/Components/Tour/Tour';
import { useLocalization } from '@/Hooks';
import { useGeneral } from './General';
import { useAdditional } from './Additional';
import { useModals } from './Modals';
import { salesPageActions } from '@/Pages/Sales/services';
import { useDispatch } from 'react-redux';

export function useSales() {
  const dispatch = useDispatch();

  const {
    tours: {
      Sales: { Default, FunctionMenuTour },
    },
  } = useLocalization();

  const general = useGeneral();
  const additional = useAdditional();
  const modals = useModals();

  const pageTour = useMemo<TourType>(
    () => ({
      id: 'pageTour',
      title: Default.Title,
      before: () => {
        dispatch(salesPageActions.setTabActiveStep({ key: 'overviewTabStep', value: 0 }));
      },
      steps: [
        {
          content: Default.Step1.Text,
          placement: 'auto',
          target: '#salesPage',
          title: Default.Step1.Title,
          disableBeacon: true,
          link: Default.Step1.DetailsLink,
        },
        {
          content: Default.Step2.Text,
          placement: 'auto',
          target: '#salesPageSettings',
          title: Default.Step2.Title,
          disableBeacon: true,
          link: Default.Step2.DetailsLink,
        },
        {
          content: Default.Step3.Text,
          placement: 'auto',
          target: '.salesTable',
          title: Default.Step3.Title,
          disableBeacon: true,
          link: Default.Step3.DetailsLink,
        },
        {
          content: Default.Step4.Text,
          placement: 'auto',
          target: '#tableFilters',
          title: Default.Step4.Title,
          disableBeacon: true,
          link: Default.Step4.DetailsLink,
        },
        {
          content: Default.Step5.Text,
          placement: 'bottom',
          target: '#crudExtended',
          title: Default.Step5.Title,
          disableBeacon: true,
          link: Default.Step5.DetailsLink,
          styles: {
            spotlight: {
              minWidth: '300px',
            },
          },
        },
        {
          content: Default.Step6.Text,
          placement: 'auto',
          target: '#headerMenu',
          title: Default.Step6.Title,
          disableBeacon: true,
          link: Default.Step6.DetailsLink,
        },
        {
          content: Default.Step7.Text,
          placement: 'auto',
          target: '#generalInfo',
          title: Default.Step7.Title,
          disableBeacon: true,
          link: Default.Step7.DetailsLink,
        },
        {
          content: Default.Step8.Text,
          placement: 'auto',
          target: '#orderItems',
          title: Default.Step8.Title,
          disableBeacon: true,
          link: Default.Step8.DetailsLink,
        },
      ],
    }),
    [Default],
  );

  const functionMenuTour = useMemo<TourType>(
    () => ({
      id: 'functionMenuTour',
      title: FunctionMenuTour.Title,
      steps: [
        {
          content: FunctionMenuTour.Step1.Text,
          placement: 'auto',
          target: '#headerMenu',
          title: FunctionMenuTour.Step1.Title,
          disableBeacon: true,
          link: FunctionMenuTour.Step1.DetailsLink,
        },
        {
          content: FunctionMenuTour.Step2.Text,
          placement: 'auto',
          target: '#createDocuments',
          title: FunctionMenuTour.Step2.Title,
          disableBeacon: true,
          link: FunctionMenuTour.Step2.DetailsLink,
        },
        {
          content: FunctionMenuTour.Step3.Text,
          placement: 'auto',
          target: '#changePaymentStatus',
          title: FunctionMenuTour.Step3.Title,
          disableBeacon: true,
          link: FunctionMenuTour.Step3.DetailsLink,
        },
        {
          content: FunctionMenuTour.Step4.Text,
          placement: 'auto',
          target: '#changeDeliveryStatus',
          title: FunctionMenuTour.Step4.Title,
          disableBeacon: true,
          link: FunctionMenuTour.Step4.DetailsLink,
        },
        {
          content: FunctionMenuTour.Step5.Text,
          placement: 'auto',
          target: '#documentPrint',
          title: FunctionMenuTour.Step5.Title,
          disableBeacon: true,
          link: FunctionMenuTour.Step5.DetailsLink,
        },
        {
          content: FunctionMenuTour.Step6.Text,
          placement: 'auto',
          target: '#sendEmail',
          title: FunctionMenuTour.Step6.Title,
          disableBeacon: true,
          link: FunctionMenuTour.Step6.DetailsLink,
        },
        {
          content: FunctionMenuTour.Step7.Text,
          placement: 'auto',
          target: '#splitOrder',
          title: FunctionMenuTour.Step7.Title,
          disableBeacon: true,
          link: FunctionMenuTour.Step7.DetailsLink,
        },
        {
          content: FunctionMenuTour.Step8.Text,
          placement: 'auto',
          target: '#orderMerge',
          title: FunctionMenuTour.Step8.Title,
          disableBeacon: true,
          link: FunctionMenuTour.Step8.DetailsLink,
        },
        {
          content: FunctionMenuTour.Step9.Text,
          placement: 'auto',
          target: '#createFeedback',
          title: FunctionMenuTour.Step9.Title,
          disableBeacon: true,
          link: FunctionMenuTour.Step9.DetailsLink,
        },
        {
          content: FunctionMenuTour.Step10.Text,
          placement: 'auto',
          target: '#shippingLabel',
          title: FunctionMenuTour.Step10.Title,
          disableBeacon: true,
          link: FunctionMenuTour.Step10.DetailsLink,
        },
        {
          content: FunctionMenuTour.Step11.Text,
          placement: 'auto',
          target: '#changeOtherStatuses',
          title: FunctionMenuTour.Step11.Title,
          disableBeacon: true,
          link: FunctionMenuTour.Step11.DetailsLink,
        },
      ],
    }),
    [FunctionMenuTour],
  );

  return [pageTour, functionMenuTour, ...general, ...additional, ...modals];
}
